import React from 'react';
import { Box, Container, Typography, Card, CardContent, Button } from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { motion } from 'framer-motion';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import SecurityIcon from '@mui/icons-material/Security';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import GroupsIcon from '@mui/icons-material/Groups';

const useCases = [
  {
    icon: <LocalPoliceIcon sx={{ fontSize: 48 }} />,
    title: 'Law Enforcement',
    description: 'Manage case files, suspect profiles, and intelligence reports in a centralized, searchable database.',
    features: [
      'Case management system',
      'Suspect tracking',
      'Evidence cataloging',
      'Inter-department sharing',
    ],
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 48 }} />,
    title: 'Intelligence Agencies',
    description: 'Analyze patterns, track threats, and share intelligence securely across departments and agencies.',
    features: [
      'Pattern analysis',
      'Threat assessment',
      'Secure collaboration',
      'Real-time alerts',
    ],
  },
  {
    icon: <AccountBalanceIcon sx={{ fontSize: 48 }} />,
    title: 'Federal Agencies',
    description: 'Meet strict compliance requirements while maintaining operational efficiency and data security.',
    features: [
      'CJIS compliance',
      'FedRAMP ready',
      'Audit trails',
      'Access control',
    ],
  },
  {
    icon: <GroupsIcon sx={{ fontSize: 48 }} />,
    title: 'Local Departments',
    description: 'Community intelligence gathering and sharing with scalable solutions for departments of any size.',
    features: [
      'Community tips',
      'Local intelligence',
      'Resource sharing',
      'Budget-friendly',
    ],
  },
];

const UseCasesSection: React.FC = () => {
  return (
    <Box
      id="use-cases"
      sx={{
        py: 10,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        backdropFilter: 'blur(5px)',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              mb: 2,
              fontSize: { xs: '2rem', md: '2.25rem' },
            }}
          >
            Use Cases
          </Typography>
          <Typography
            variant="body1"
            align="center"
            sx={{
              mb: 6,
              color: '#9CA3AF',
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Trusted by agencies across all levels of law enforcement and intelligence
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          {useCases.map((useCase, index) => (
            <Grid item xs={12} md={6} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                style={{ height: '100%' }}
              >
                <Card
                  sx={{
                    height: '100%',
                    backgroundColor: '#0A0A0A',
                    border: '1px solid #1A1A1A',
                    p: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#1D9BF0',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(29, 155, 240, 0.2)',
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          color: '#1D9BF0',
                          mr: 2,
                        }}
                      >
                        {useCase.icon}
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          color: '#E7E9EA',
                        }}
                      >
                        {useCase.title}
                      </Typography>
                    </Box>
                    
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 3,
                        color: '#C7CDD3',
                      }}
                    >
                      {useCase.description}
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      {useCase.features.map((feature, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          sx={{
                            color: '#9CA3AF',
                            mb: 1,
                            pl: 2,
                            position: 'relative',
                            '&:before': {
                              content: '"•"',
                              position: 'absolute',
                              left: 0,
                              color: '#1D9BF0',
                            },
                          }}
                        >
                          {feature}
                        </Typography>
                      ))}
                    </Box>

                    <Button
                      variant="outlined"
                      color="primary"
                      sx={{ mt: 2 }}
                    >
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default UseCasesSection;